import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import axios from "../api/axiosConfig";
import { toast } from "react-toastify";
import {
  ArrowLeft, MapPin, CheckCircle, CreditCard, MessageSquare,
  Loader, Banknote, Smartphone, Truck, Store, Bike, Tag, Plus, Pencil, X,
} from "lucide-react";
import AddressForm from "../components/forms/AddressForm";

// ── Opciones de tipo de entrega ────────────────────────────────────────────────
const DELIVERY_OPTIONS = [
  {
    value: "domicilio_general",
    label: "Domicilio general",
    desc: "Ryder de la plataforma",
    icon: Truck,
    color: "blue",
  },
  {
    value: "domiciliario_tienda",
    label: "Domiciliario tienda",
    desc: "Repartidor propio, envío gratis",
    icon: Bike,
    color: "green",
    requiresOwnDelivery: true,
  },
  {
    value: "recoger_en_tienda",
    label: "Recoger en tienda",
    desc: "Recoge sin costo",
    icon: Store,
    color: "violet",
  },
];

const COLOR_MAP = {
  blue:   { border: "border-blue-500",   bg: "bg-blue-50",   icon: "text-blue-600"   },
  green:  { border: "border-green-500",  bg: "bg-green-50",  icon: "text-green-600"  },
  violet: { border: "border-violet-500", bg: "bg-violet-50", icon: "text-violet-600" },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();

  const [user,            setUser]            = useState(null);
  const [addresses,       setAddresses]       = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod,   setPaymentMethod]   = useState("efectivo");
  const [notes,           setNotes]           = useState("");
  const [loading,         setLoading]         = useState(false);
  const [availableMethods, setAvailableMethods] = useState(["efectivo"]);

  // Entrega
  const [deliveryType,  setDeliveryType]  = useState("domicilio_general");
  const [company,       setCompany]       = useState(null);
  const [sedes,         setSedes]         = useState([]);
  const [selectedSede,  setSelectedSede]  = useState(null);

  // Cupón
  const [couponCode,     setCouponCode]     = useState("");
  const [couponData,     setCouponData]     = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading,  setCouponLoading]  = useState(false);

  // QPUNTOS
  const [useQPoints,   setUseQPoints]   = useState(false);
  const [qpointsToUse, setQpointsToUse] = useState(0);

  // Tarifa estimada por distancia
  const [estimatedFee,      setEstimatedFee]      = useState(null);
  const [estimatedDistance, setEstimatedDistance] = useState(null);
  const [feeLoading,        setFeeLoading]        = useState(false);

  // Dirección inline
  const [addrFormOpen, setAddrFormOpen] = useState(false);  // 'new' | id | false

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getFinalPrice = (item) =>
    item.offerPrice && item.offerPrice > 0 && item.offerPrice < item.price
      ? item.offerPrice : item.price;

  const subtotal             = cart.reduce((s, i) => s + getFinalPrice(i) * i.qty, 0);
  const totalWithoutDiscount = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const productDiscount      = totalWithoutDiscount - subtotal;

  const getCompanyId  = (item) => item.companyId ?? item.company?.id ?? item.company?._id;
  const companyIds    = [...new Set(cart.map(getCompanyId).filter(Boolean))];
  const singleCompany = companyIds.length === 1 ? companyIds[0] : null;
  const canUseQPoints = companyIds.length === 1;

  const effectiveDeliveryFee =
    deliveryType !== "domicilio_general"
      ? 0
      : estimatedFee !== null
        ? estimatedFee
        : Number(company?.deliveryFee ?? 5000);

  // ── Cargar usuario ───────────────────────────────────────────────────────────
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user") || "null");
    if (!userData) {
      toast.error("Debes iniciar sesión para continuar");
      navigate("/login");
      return;
    }
    setUser(userData);
    fetchUserData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (cart.length === 0) navigate("/cart");
    // eslint-disable-next-line
  }, [cart]);

  // ── Cargar métodos de pago habilitados ────────────────────────────────────────
  useEffect(() => {
    axios.get("/settings/payment-methods")
      .then((res) => {
        const methods = res.data?.data?.methods;
        if (Array.isArray(methods) && methods.length > 0) {
          setAvailableMethods(methods);
          // Si el método seleccionado ya no está disponible, usar el primero
          setPaymentMethod((prev) => methods.includes(prev) ? prev : methods[0]);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line
  }, []);

  // ── Cargar empresa ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!singleCompany) return;
    axios.get(`/companies/${singleCompany}`)
      .then((res) => setCompany(res.data?.data?.company ?? res.data?.data ?? res.data))
      .catch(() => {});
    axios.get(`/sedes/company/${singleCompany}`)
      .then((res) => {
        const raw = res.data?.data?.sedes ?? res.data?.data ?? res.data ?? [];
        setSedes(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {});
  }, [singleCompany]);

  // Si el tipo seleccionado ya no está disponible, reset
  useEffect(() => {
    if (deliveryType === "domiciliario_tienda" && company && !company.hasOwnDelivery) {
      setDeliveryType("domicilio_general");
    }
  }, [company, deliveryType]);

  // Estimar tarifa de domicilio por distancia
  useEffect(() => {
    if (deliveryType !== "domicilio_general" || !selectedAddress || !singleCompany) {
      setEstimatedFee(null);
      setEstimatedDistance(null);
      return;
    }
    const lat = selectedAddress.lat ?? selectedAddress.location?.lat;
    const lng = selectedAddress.lng ?? selectedAddress.location?.lng;
    if (!lat || !lng) return;
    setFeeLoading(true);
    axios
      .get(`/delivery-pricing/estimate?companyId=${singleCompany}&lat=${lat}&lng=${lng}`)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        if (d?.fee != null) {
          setEstimatedFee(d.fee);
          setEstimatedDistance(d.roadKm ? Number(d.roadKm).toFixed(1) : null);
        }
      })
      .catch(() => {})
      .finally(() => setFeeLoading(false));
  // eslint-disable-next-line
  }, [deliveryType, selectedAddress, singleCompany]);

  const fetchUserData = async () => {
    try {
      const res = await axios.get("/users/me");
      const addrs = res.data.addresses || [];
      setAddresses(addrs);
      setUser(res.data);
      if (addrs.length > 0) {
        const def = addrs.find((a) => a.isDefault);
        setSelectedAddress((prev) => {
          if (prev) return addrs.find((a) => (a.id ?? a._id) === (prev.id ?? prev._id)) ?? def ?? addrs[0];
          return def ?? addrs[0];
        });
      }
    } catch { toast.error("Error cargando direcciones"); }
  };

  const saveAddress = async (data) => {
    try {
      if (addrFormOpen && addrFormOpen !== "new") {
        await axios.put(`/users/me/address/${addrFormOpen}`, data);
        toast.success("Dirección actualizada");
      } else {
        await axios.post("/users/me/address", data);
        toast.success("Dirección agregada");
      }
      setAddrFormOpen(false);
      await fetchUserData();
    } catch { toast.error("Error guardando dirección"); }
  };

  // ── Cupón ─────────────────────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { toast.error("Ingresa un código de cupón"); return; }
    if (!singleCompany)     { toast.error("El cupón solo aplica para una empresa"); return; }
    setCouponLoading(true);
    try {
      const res = await axios.post("/coupons/validate", {
        code: couponCode, companyId: singleCompany, orderAmount: subtotal,
      });
      const result = res.data?.data ?? res.data;
      setCouponData(result);
      setCouponDiscount(result?.discount || 0);
      toast.success("Cupón aplicado 🎉");
    } catch (err) {
      setCouponData(null);
      setCouponDiscount(0);
      toast.error(err.response?.data?.message || "Cupón inválido");
    } finally { setCouponLoading(false); }
  };

  // ── Totales ───────────────────────────────────────────────────────────────────
  const totalWithCoupon  = Math.max(subtotal + effectiveDeliveryFee - couponDiscount, effectiveDeliveryFee);
  const maxQPointsUsable = Math.min(user?.qpoints || 0, totalWithCoupon);
  const totalWithQPoints = Math.max(totalWithCoupon - qpointsToUse, 0);

  // ── Crear pedido ──────────────────────────────────────────────────────────────
  const handleCreateOrder = async () => {
    const itemsByCompany = {};
    cart.forEach((item) => {
      const cid = getCompanyId(item);
      if (!cid) return;
      if (!itemsByCompany[cid]) itemsByCompany[cid] = [];
      itemsByCompany[cid].push({ productId: item.id ?? item._id, quantity: item.qty, note: item.note || '' });
    });

    if (deliveryType === "recoger_en_tienda") {
      if (!selectedSede) { toast.error("Selecciona una sede para recoger"); return; }
    } else {
      if (!selectedAddress) { toast.error("Selecciona una dirección de entrega"); return; }
      const lat = parseFloat(selectedAddress?.lat ?? selectedAddress?.location?.lat);
      const lng = parseFloat(selectedAddress?.lng ?? selectedAddress?.location?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        toast.error("La dirección no tiene coordenadas válidas"); return;
      }
    }

    setLoading(true);
    try {
      const orderPromises = Object.entries(itemsByCompany).map(([cid, items]) => {
        const appliedCoupon = couponData && cid === singleCompany ? couponCode : undefined;
        const base = {
          companyId: cid, items, deliveryType, paymentMethod, notes,
          couponCode: appliedCoupon,
          qpointsToUse: canUseQPoints && useQPoints ? qpointsToUse : 0,
        };
        if (deliveryType === "recoger_en_tienda") {
          return axios.post("/orders", { ...base, sedeId: selectedSede.id });
        }
        const lat = parseFloat(selectedAddress?.lat ?? selectedAddress?.location?.lat);
        const lng = parseFloat(selectedAddress?.lng ?? selectedAddress?.location?.lng);
        return axios.post("/orders", {
          ...base,
          deliveryAddress: selectedAddress.address,
          deliveryCity:    selectedAddress.city,
          ...(user?.phone ? { deliveryPhone: `${user.phoneCountryCode ?? ''}${user.phone}`.trim() } : {}),
          deliveryLat:     lat,
          deliveryLng:     lng,
          deliveryLabel:   selectedAddress.label,
        });
      });
      await Promise.all(orderPromises);
      toast.success("¡Pedido creado exitosamente!");
      clearCart();
      navigate("/myaccount");
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors?.length) {
        const first = data.errors[0];
        toast.error(`Error en "${first.field}": ${first.message}`);
      } else {
        toast.error(data?.message || "Error al crear pedido");
      }
    } finally { setLoading(false); }
  };

  const availableOptions = DELIVERY_OPTIONS.filter(
    (opt) => !opt.requiresOwnDelivery || company?.hasOwnDelivery
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto">

        <button onClick={() => navigate("/cart")}
          className="mb-5 flex items-center gap-2 text-supernova-600 text-sm font-semibold">
          <ArrowLeft size={16} /> Volver al carrito
        </button>

        <h1 className="text-2xl font-bold mb-6 text-gray-900">Finalizar compra</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ══ IZQUIERDA ══ */}
          <div className="lg:col-span-2 space-y-4">

            {/* ── Tipo de entrega ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-3">
                ¿Cómo quieres recibirlo?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {availableOptions.map((opt) => {
                  const c = COLOR_MAP[opt.color];
                  const active = deliveryType === opt.value;
                  return (
                    <button key={opt.value} onClick={() => setDeliveryType(opt.value)}
                      className={`relative flex flex-col items-start gap-1 p-4 rounded-xl border-2 text-left transition
                        ${active ? `${c.border} ${c.bg}` : "border-gray-100 hover:border-gray-200 bg-white"}`}
                    >
                      <opt.icon size={18} className={active ? c.icon : "text-gray-400"} />
                      <span className={`text-sm font-semibold ${active ? "text-gray-900" : "text-gray-600"}`}>
                        {opt.label}
                      </span>
                      <span className="text-[11px] text-gray-400 leading-tight">{opt.desc}</span>
                      <span className={`text-[11px] font-bold ${
                        opt.value === "domicilio_general"
                          ? (active ? c.icon : "text-gray-400")
                          : "text-green-600"
                      }`}>
                        {opt.value === "domicilio_general"
                          ? feeLoading
                            ? "Calculando..."
                            : `+$${effectiveDeliveryFee.toLocaleString("es-CO")}`
                          : "Gratis"}
                      </span>
                      {active && <CheckCircle size={13} className={`${c.icon} absolute top-2 right-2`} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Sede (pickup) ── */}
            {deliveryType === "recoger_en_tienda" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h2 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-3">
                  Selecciona la sede
                </h2>
                {sedes.length === 0
                  ? <p className="text-sm text-gray-400">No hay sedes disponibles</p>
                  : sedes.map((sede) => (
                    <div key={sede.id} onClick={() => setSelectedSede(sede)}
                      className={`p-3 border-2 rounded-xl cursor-pointer mb-2 transition
                        ${selectedSede?.id === sede.id
                          ? "border-violet-500 bg-violet-50"
                          : "border-gray-100 hover:border-gray-200"}`}
                    >
                      <p className="text-sm font-semibold text-gray-800">{sede.name}</p>
                      <p className="text-xs text-gray-500">{sede.address}</p>
                    </div>
                  ))
                }
              </div>
            )}

            {/* ── Dirección ── */}
            {deliveryType !== "recoger_en_tienda" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-sm text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <MapPin size={14} /> Dirección de entrega
                  </h2>
                  {!addrFormOpen && (
                    <button
                      onClick={() => setAddrFormOpen("new")}
                      className="flex items-center gap-1 text-xs font-bold text-supernova-600 bg-supernova-50 hover:bg-supernova-100 px-2.5 py-1.5 rounded-lg transition"
                    >
                      <Plus size={12} /> Agregar
                    </button>
                  )}
                </div>

                {/* Alerta si no tiene direcciones y no está agregando */}
                {addresses.length === 0 && !addrFormOpen && (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
                    <MapPin size={16} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800 font-medium">
                      No tienes ninguna dirección de entrega. Agrega una para continuar.
                    </p>
                  </div>
                )}

                {/* Formulario inline agregar / editar */}
                {addrFormOpen && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-gray-700">
                        {addrFormOpen === "new" ? "Nueva dirección" : "Editar dirección"}
                      </p>
                      <button onClick={() => setAddrFormOpen(false)} className="text-gray-400 hover:text-gray-600">
                        <X size={15} />
                      </button>
                    </div>
                    <AddressForm
                      initial={addrFormOpen !== "new" ? addresses.find((a) => (a.id ?? a._id) === addrFormOpen) ?? {} : {}}
                      onSubmit={saveAddress}
                      onCancel={() => setAddrFormOpen(false)}
                    />
                  </div>
                )}

                {/* Lista de direcciones */}
                {addresses.map((addr) => {
                  const addrId = addr.id ?? addr._id;
                  const isSelected = (selectedAddress?.id ?? selectedAddress?._id) === addrId;
                  return (
                    <div key={addrId}
                      className={`p-3 border-2 rounded-xl mb-2 transition ${
                        isSelected ? "border-supernova-500 bg-supernova-50" : "border-gray-100 hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex-1 cursor-pointer" onClick={() => setSelectedAddress(addr)}>
                          <p className="text-sm font-semibold text-gray-800">{addr.label}</p>
                          <p className="text-xs text-gray-500">{addr.address}</p>
                        </div>
                        <button
                          onClick={() => setAddrFormOpen(addrFormOpen === addrId ? false : addrId)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-supernova-600 hover:bg-gray-100 transition flex-shrink-0"
                        >
                          <Pencil size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Pago ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <CreditCard size={14} /> Método de pago
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: "efectivo",  l: "Efectivo",  i: Banknote   },
                  { v: "nequi",     l: "Nequi",     i: Smartphone },
                  { v: "daviplata", l: "Daviplata", i: Smartphone },
                  { v: "tarjeta",   l: "Tarjeta",   i: CreditCard },
                ].filter((m) => availableMethods.includes(m.v)).map((m) => (
                  <div key={m.v} onClick={() => setPaymentMethod(m.v)}
                    className={`p-4 border-2 rounded-xl flex items-center gap-2 cursor-pointer transition
                      ${paymentMethod === m.v
                        ? "border-supernova-500 bg-supernova-50"
                        : "border-gray-100 hover:border-gray-200"}`}
                  >
                    <m.i size={15} className={paymentMethod === m.v ? "text-supernova-600" : "text-gray-400"} />
                    <span className={`text-sm font-medium ${paymentMethod === m.v ? "text-gray-900" : "text-gray-600"}`}>
                      {m.l}
                    </span>
                    {paymentMethod === m.v && <CheckCircle size={13} className="ml-auto text-supernova-600" />}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Notas ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-semibold text-sm text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MessageSquare size={14} /> Notas
              </h2>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Instrucciones especiales, referencias, etc."
                className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300 resize-none"
                rows={3} />
            </div>
          </div>

          {/* ══ DERECHA — RESUMEN ══ */}
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 lg:sticky lg:top-4">
              <h2 className="font-bold text-base text-gray-800 mb-4">Resumen</h2>

              {/* Cupón */}
              <div className="flex gap-2 mb-4">
                <input value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Código de cupón"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300" />
                <button onClick={handleApplyCoupon} disabled={couponLoading}
                  className="flex items-center gap-1 px-4 py-2.5 bg-gray-800 text-white text-xs font-semibold rounded-lg hover:bg-gray-700 transition">
                  <Tag size={12} />
                  {couponLoading ? "..." : "Aplicar"}
                </button>
              </div>

              {/* Desglose */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toLocaleString("es-CO")}</span>
                </div>
                {productDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Ahorro productos</span>
                    <span>-${productDiscount.toLocaleString("es-CO")}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Desc. cupón</span>
                    <span>-${couponDiscount.toLocaleString("es-CO")}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span className="flex flex-col">
                    <span>Domicilio</span>
                    {estimatedDistance && (
                      <span className="text-xs text-gray-400">~{estimatedDistance} km recorridos</span>
                    )}
                  </span>
                  <span className={effectiveDeliveryFee === 0 ? "text-green-600 font-semibold" : ""}>
                    {feeLoading
                      ? <Loader size={12} className="animate-spin inline" />
                      : effectiveDeliveryFee === 0
                        ? "Gratis"
                        : `$${effectiveDeliveryFee.toLocaleString("es-CO")}`}
                  </span>
                </div>
                <hr className="border-gray-100" />

                {/* QPUNTOS */}
                {user?.qpoints > 0 && canUseQPoints && (
                  <div className="bg-supernova-50 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">Usar QPUNTOS</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={useQPoints}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUseQPoints(checked);
                            setQpointsToUse(checked ? maxQPointsUsable : 0);
                          }} />
                        <span className="text-xs text-gray-500">
                          {(user.qpoints || 0).toLocaleString()} pts
                        </span>
                      </label>
                    </div>
                    {useQPoints && (
                      <div className="flex justify-between text-green-700 text-xs">
                        <span>Desc. QPUNTOS</span>
                        <span>-${qpointsToUse.toLocaleString("es-CO")}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between text-base font-bold text-gray-900 pt-1">
                  <span>Total</span>
                  <span className="text-supernova-600">${totalWithQPoints.toLocaleString("es-CO")}</span>
                </div>
              </div>

              <button onClick={handleCreateOrder} disabled={loading}
                className="mt-5 w-full bg-supernova-500 hover:bg-supernova-600 active:scale-95 text-white py-3 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2">
                {loading ? <Loader size={16} className="animate-spin" /> : "Confirmar pedido"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
